import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError, of, BehaviorSubject } from 'rxjs';
import { switchMap, catchError, share, tap } from 'rxjs/operators';
import * as cuid from 'cuid';
import { environment } from 'src/environments/environment';
import { CookiesService } from 'src/app/services/cookies.service';
import { HCaptchaService } from 'src/app/services/hcaptcha.service';

export enum GrantType {
  Password = 'password',
  RefreshToken = 'refresh_token'
}

export enum OtpType {
  TOTP = 'totp',
  SMS = 'sms',
  CALL = 'call'
}

export interface AuthResponse {
  ipVerified?: boolean;
  verificationSent?: boolean;
  supportedTypes?: OtpType[];
  otpPhone?: string;
  error?: boolean;
  errorCode?: string;
  errorDescription?: string;
}

export interface AuthToken {
  accessToken: string;
  tokenType: string;
  refreshToken: string;
  expiresIn: number;
  userId: string;
  scope: string[];
  sessionId: string;
}

export enum AuthStatus {
  LoggedIn,
  LoggedOff,
  AuthError,
  RefreshingToken,
  LoginPending,
  Locked
}

export enum AuthError {
  Error,
  Locked
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  static readonly AUTH_URL = `${environment.api.url}/oauth/token`;
  static readonly REVOKE_URL = `${environment.api.url}/oauth/logout`;

  private status = new BehaviorSubject<AuthStatus>(AuthStatus.LoggedOff);

  private clientId = environment.api.clientId;
  private clientSecret = environment.api.clientSecret;
  private email: string;
  private password: string;
  private sessionId: string;

  private token: AuthToken;

  private refreshRequest: Observable<any>;

  constructor(
    private http: HttpClient,
    private cookiesService: CookiesService,
    private hCaptchaService: HCaptchaService,
  ) {
    // Even if user logsin with two different accounts from same device, sessionID needs to be the same to make the connection
    this.initiateSessionId();

    // If a tab changes the token, other tabs needs to be notified of that change
    window.addEventListener('storage', (event) => {
      if (event.key === 'token') {
        this.setToken(JSON.parse(event.newValue));
      }
    });
  }

  setCredentials(email: string, password: string): AuthService {
    this.email = email;
    this.password = password;

    return this;
  }

  login(captcha: string = null): Observable<any> {
    return this.auth(null, captcha);
  }

  sendCode(otp: OtpType) {
    return this.auth(new HttpHeaders({
      'X-BO-OTP-TYPE': otp
    }));
  }

  confirmOtpCode(type: OtpType, value: string, remember: boolean = false) {
    return this.auth(new HttpHeaders({
      'X-BO-OTP-TYPE': type,
      'X-BO-OTP-VALUE': value,
      'X-BO-OTP-REMEMBER': remember ? '1' : '0'
    }));
  }

  refreshToken() {
    if (!this.refreshRequest) {
      this.refreshRequest = this.auth(null, null, GrantType.RefreshToken);
    }

    return this.refreshRequest;
  }

  private auth(headers?: HttpHeaders, captcha: string = null, grantType = GrantType.Password): Observable<any> {
    let payload = '';

    switch (grantType) {
      case GrantType.Password:
        if (this.email === null || this.password === null) {
          return throwError({error: AuthError.Error, error_description: 'Empty email and/or password'});
        }
        // tslint:disable-next-line:max-line-length
        payload = `username=${encodeURIComponent(this.email)}&password=${encodeURIComponent(this.password)}&grant_type=password&client_id=${this.clientId}&client_secret=${this.clientSecret}`;
        break;
      case GrantType.RefreshToken:
        const token = this.getToken(true);
        if (!token || !token.refreshToken) {
          this.clearToken();
          return throwError({error: AuthError.Error, error_description: 'Empty refresh token'});
        }
        // tslint:disable-next-line:max-line-length
        payload = `refresh_token=${token.refreshToken}&grant_type=refresh_token&client_id=${this.clientId}&client_secret=${this.clientSecret}`;
        this.status.next(AuthStatus.RefreshingToken);
        break;
      default:
        return throwError({error: AuthError.Error, error_description: 'Unkown grant type'});
    }

    if (!headers) {
      headers = new HttpHeaders();
    }

    headers = headers.append('Content-Type', 'application/x-www-form-urlencoded');
    headers = headers.append('X-BO-SESSION-ID', this.sessionId || '');
    headers = headers.append('X-BO-OAUTH-SESSION-ID', this.sessionId || '');
    headers = headers.append('X-BO-CLIENT-TYPE', environment.api.type);
    headers = headers.append('X-BO-CLIENT-VERSION', environment.version);

    const captchaHeader = captcha || this.hCaptchaService.passiveCaptcha;
    if (captchaHeader && GrantType.RefreshToken !== grantType) {
      headers = headers.append('X-BO-CAPTCHA-FACTOR', captchaHeader);
    }

    return this.http.post(AuthService.AUTH_URL,
      payload, {
        headers,
        observe: 'response',
      }).pipe(
      tap(() => {
        this.refreshRequest = null;
      }),
      this.catchSuccess(),
      this.catchError(),
      share()
    );
  }

  private revokeToken(): Observable<any> {
    const token = this.getToken();

    if (!token || !token.accessToken) {
      return throwError({error: AuthError.Error, error_description: 'Logout error: No token found'});
    }

    let headers = new HttpHeaders();
    headers = headers.append('Content-Type', 'application/x-www-form-urlencoded');
    headers = headers.append('X-BO-SESSION-ID', this.sessionId || '');
    headers = headers.append('X-BO-OAUTH-SESSION-ID', this.sessionId || '');
    headers = headers.append('Authorization', `Bearer ${token.accessToken}`);

    return this.http.post(AuthService.REVOKE_URL, '', {
      headers,
      observe: 'response',
    });
  }

  catchSuccess() {
    return switchMap((response) => {
      this.setToken({
        accessToken: response['body']['access_token'],
        expiresIn: response['body']['expires_in'],
        refreshToken: response['body']['refresh_token'],
        scope: response['body']['scope'],
        tokenType: response['body']['token_type'],
        userId: response['body']['user_id'],
        sessionId: this.sessionId
      });

      this.status.next(AuthStatus.LoggedIn);

      return of(this.token);
    });
  }

  catchError() {
    return catchError((err: HttpErrorResponse) => {
      if (err.status === 403) {
        this.status.next(AuthStatus.LoginPending);
        const supportedTypes = err.headers.get('X-BO-OTP-SUPPORTED-TYPES');

        return of({
          ipVerified: err.headers.get('X-BO-IP-VERIFIED') === '1',
          verificationSent: err.headers.get('X-BO-IP-VERIFICATION-SENT') === '1',
          supportedTypes: supportedTypes ? supportedTypes.split(',') : [],
          otpPhone: err.headers.get('X-BO-OTP-PHONE'),
          errorCode: err && err.error ? err.error.error : '',
          errorDescription: err && err.error ? err.error.error_description : ''
        });
      }

      this.clearToken();
      this.status.next(AuthStatus.AuthError);
      return throwError({
        error: AuthError.Error,
        errorDescription: 'Something went wrong, try again'
      });
    });
  }

  getToken(forceRefresh = false): AuthToken {
    try {
      if (forceRefresh || !this.token) {
        this.token = JSON.parse(localStorage.getItem('token'));
      }

      return this.token;
    } catch (error) {
      return null;
    }
  }

  getSessionId() {
    return this.sessionId;
  }

  setTokenFromCookie() {
    const accessToken = this.cookiesService.get('token');

    if (!accessToken) {
      return;
    }

    const refreshToken = this.cookiesService.get("refresh_token");
    const sessionId = this.cookiesService.get("session_id");

    const tokenFromCookie = { accessToken, refreshToken, sessionId };
    const existingToken = this.getToken();
    this.setToken({ ...existingToken, ...tokenFromCookie });

    // Remove cookies
    this.cookiesService.remove('token');
    this.cookiesService.remove("refresh_token");
    this.cookiesService.remove("session_id");
  }

  clearToken() {
    this.token = null;
    localStorage.removeItem('token');
  }

  logout(): void {
    this.revokeToken().subscribe(() => {
      this.clearToken();
      this.status.next(AuthStatus.LoggedOff);
    });
  }

  get statusChanges() { return this.status.asObservable(); }

  private setToken(token: AuthToken) {
    this.token = token;
    localStorage.setItem('token', JSON.stringify(token));
  }

  private initiateSessionId() {
    this.sessionId = localStorage.getItem('sessionId');
    if (!this.sessionId) {
      this.sessionId = cuid();
      localStorage.setItem('sessionId', this.sessionId);
    }
  }
}
